import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/api";
import { ProgressBar } from "@/components/OwnToggle";
import { useI18n } from "@/i18n";
import { useSeo } from "@/seo";

const DASH = "—";
const val = (v) => (v === null || v === undefined || v === "" ? DASH : v);
const fmt = (d, lang) => (d ? new Date(d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "short", day: "numeric" }) : DASH);

export function SetStat({ label, value, testid }) {
  return (
    <div className="border-l border-[var(--wl-line)] pl-2" data-testid={testid}>
      <div className="wl-mono text-[9px] uppercase tracking-[.06em] text-[var(--wl-muted)]">{label}</div>
      <div className="wl-mono text-[13px] font-bold text-[var(--wl-text)]">{val(value)}</div>
    </div>
  );
}

export default function SetIndex() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const manufacturer = params.get("manufacturer") || "";
  const season = params.get("season") || "";
  useSeo(t("sets.index_title"), t("sets.index_desc"), "/sets");

  useEffect(() => {
    const p = {};
    if (manufacturer) p.manufacturer = manufacturer;
    if (season) p.season = season;
    if (params.get("q")) p.q = params.get("q");
    // Une requête interrompue n'est pas un catalogue vide : on réessaie une fois, on ne
    // fabrique jamais `total: 0` (même règle que la watchlist, 22/09/2026).
    let alive = true;
    const ask = (tries) => {
      api.get("/sets", { params: p })
        .then((r) => { if (alive) setData(r.data); })
        .catch(() => { if (alive && tries > 0) setTimeout(() => ask(tries - 1), 1500); });
    };
    ask(2);
    return () => { alive = false; };
  }, [manufacturer, season, params]);

  const [cardHits, setCardHits] = useState([]);
  // Barre de progression sur chaque set ENTAMÉ, pour un visiteur connecté. Endpoint léger,
  // servi aussi aux déconnectés (qui reçoivent un objet vide) : jamais de 401 ici.
  const [progress, setProgress] = useState({});
  useEffect(() => {
    api.get("/collection/progress").then((r) => setProgress(r.data?.sets || {})).catch(() => setProgress({}));
  }, []);
  useEffect(() => {
    const term = params.get("q");
    if (!term) { setCardHits([]); return; }
    api.get("/cards/search", { params: { q: term } }).then((r) => setCardHits(r.data.cards || [])).catch(() => setCardHits([]));
  }, [params]);

  const setFilter = (k, v) => {
    const next = Object.fromEntries(params.entries());
    if (v) next[k] = v; else delete next[k];
    setParams(next);
  };

  return (
    <div className="wl-scope -mx-4 -mb-16 min-h-[80vh] px-4 pt-6 pb-10 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-testid="sets-index-page">
      <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-[var(--wl-text)] sm:text-3xl">{t("sets.index_h1")}</h1>
      <p className="wl-mono mt-1 text-[10.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]">{t("sets.index_sub")}</p>
      {/* La promesse du compteur, en haut de page : elle était invisible pour qui arrivait ici
          sans savoir que les cases à cocher existent. */}
      <p className="mt-2 font-sg text-[13px] font-bold uppercase tracking-[-0.02em] text-[var(--wl-text)] sm:text-[15px]" data-testid="sets-collection-promise">
        {t("sets.coll_promise")}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="sets-filters">
        <form onSubmit={(e) => { e.preventDefault(); setFilter("q", q.trim()); }} className="flex">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sets.search_ph")} className="wl-mono w-56 border border-[var(--wl-line)] bg-[var(--wl-panel)] px-3 py-1.5 text-[11px] text-[var(--wl-text)] outline-none focus:border-[var(--wl-accent)]" data-testid="sets-search-input" />
          <button type="submit" className="wl-mono border border-l-0 border-[var(--wl-line)] px-3 text-[10px] uppercase tracking-[.06em] text-[var(--wl-muted)] hover:border-[var(--wl-accent)]" data-testid="sets-search-submit">{t("sets.search")}</button>
        </form>
        <select value={manufacturer} onChange={(e) => setFilter("manufacturer", e.target.value)} className="wl-mono border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 py-1.5 text-[10.5px] text-[var(--wl-text)]" data-testid="sets-filter-manufacturer">
          <option value="">{t("sets.manufacturer")} — {t("sets.any")}</option>
          {(data?.facets?.manufacturers || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={season} onChange={(e) => setFilter("season", e.target.value)} className="wl-mono border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 py-1.5 text-[10.5px] text-[var(--wl-text)]" data-testid="sets-filter-season">
          <option value="">{t("sets.season")} — {t("sets.any")}</option>
          {(data?.facets?.seasons || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="wl-mono ml-auto text-[9px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="sets-total">{data ? `${data.total} · ${t("sets.sets")}` : ""}</span>
      </div>

      {data && data.total === 0 && <p className="wl-mono mt-8 text-[10.5px] uppercase text-[var(--wl-muted)]" data-testid="sets-empty">{t("sets.empty")}</p>}

      {cardHits.length > 0 && (
        <section className="wl-panel mt-4 p-3" data-testid="card-hits">
          <h2 className="font-display text-base font-bold uppercase tracking-tight text-[var(--wl-text)]">{t("sets.card_hits")}</h2>
          <ul className="wl-mono mt-2 text-[10.5px]">
            {cardHits.map((c) => (
              <li key={c.id} className="border-b border-[var(--wl-line)]/60 py-1">
                <Link to={`/cards/${c.id}`} className="text-[var(--wl-text)] hover:text-[var(--wl-accent)]" data-testid={`card-hit-${c.id}`}>
                  {val(c.card_number)} · {c.player_name_raw} <span className="text-[var(--wl-muted)]">· {c.set_name} {c.season} · {c.subset || c.parallel}{c.is_rookie ? " · RC" : ""}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3" data-testid="sets-grid">
        {(data?.sets || []).map((s) => (
          <Link key={s.slug} to={`/cards/${s.slug}`} className="wl-panel block p-3 transition-colors hover:border-[var(--wl-accent)]" data-testid={`set-card-${s.slug}`}>
            <div className="wl-mono text-[9px] uppercase tracking-[.06em] text-[var(--wl-muted)]">{s.manufacturer} · {s.season_short || s.season}</div>
            <div className="font-display mt-1 text-[15px] font-bold uppercase leading-tight tracking-tight text-[var(--wl-text)]">{s.set_name}</div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              <SetStat label={t("sets.cards")} value={s.total_cards} />
              <SetStat label="RC" value={s.rc_declared === false ? "n/r" : s.rc_cards} />
              <SetStat label={t("sets.subsets")} value={s.subsets} />
              <SetStat label={t("sets.imported")} value={fmt(s.imported_at, lang)} />
            </div>
            {s.completeness?.verified && (
              <div className="wl-mono mt-2 text-[9px] uppercase tracking-[.04em] text-[var(--wl-accent)]">{t("sets.verified_short")}</div>
            )}
            {progress[s.slug] && (
              <div className="mt-2.5 border-t border-[var(--wl-line)] pt-2" data-testid={`set-progress-${s.slug}`}>
                <div className="flex items-baseline justify-between">
                  <span className="wl-mono text-[9px] uppercase tracking-[.06em] text-[var(--wl-muted)]">{t("sets.coll_progress")}</span>
                  <span className="wl-mono text-[10px] font-bold tabular-nums text-[var(--wl-accent)]">
                    {t("sets.coll_owned_of").replace("{owned}", progress[s.slug].owned).replace("{total}", progress[s.slug].total).replace("{pct}", progress[s.slug].pct)}
                  </span>
                </div>
                <div className="mt-1">
                  <ProgressBar owned={progress[s.slug].owned} total={progress[s.slug].total} pct={progress[s.slug].pct} compact testid={`set-progress-bar-${s.slug}`} />
                </div>
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
